import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["inverse"];
import { Toast as DrywallToast } from 'drywall';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { STATUS_TYPES } from '../../constants';
import { ToastMixin } from '../../mixins/components/ToastMixin';
import { statusIconAppearance } from '../../util/statusIconAppearance';
import { CloseButton } from '../CloseButton/CloseButton';

/**
 * Toasts use the `ToastProvider` component to
 * manage the state of all toasts  within the application. _Always_ use toasts with
 * `ToastProvider`, _never_ embed a toast directly into the content of a page. For inline
 * status messages, you can use the `Alert` or `InlineFeedback` components.
 *
 * @related ['Alert', 'Banner', 'InlineFeedback']
 * @subcomponents ['ToastProvider']
 * @since 7.4.0
 */
var Toast = /*#__PURE__*/styled(function (_ref) {
  var inverse = _ref.inverse,
    rest = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(DrywallToast, rest);
}).withConfig({
  displayName: "Toast",
  componentId: "c11n-8-107-0__sc-18t8o9m-0"
})(["", ""], ToastMixin);
Toast.displayName = 'Toast';
Toast.propTypes = {
  /**
   * An optional action button.
   */
  actionButton: PropTypes.node,
  /**
   * The type of toast to display.
   *
   * Note: The `appearance` value can modify component content such as `icon`,
   * as well as behavior such as the `duration` it is visible with `ToastProvider`.
   */
  appearance: PropTypes.oneOf(Object.values(STATUS_TYPES)),
  /**
   * The body content of the toast message.
   */
  body: PropTypes.node,
  /**
   * In general, you will want to use the `body` prop,
   * but you can use the children prop to override the default component layout.
   * When passing a function, the function will receive all the component props as the only
   * argument (Note: some of the props may have been modified and/or enhanced,
   * so you should always use the values that are passed to the render function).
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * The close button node. You can set this to null to remove the close button from the toast.
   */
  closeButton: PropTypes.node,
  /**
   * The icon node. By default, this will use an icon based on the toast `appearance`.
   */
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * Toasts are a controlled component where the `isOpen` prop controls the visibility.
   */
  isOpen: PropTypes.bool,
  /**
   * Function called with the `closeButton` is clicked.
   */
  onClose: PropTypes.func,
  /**
   * Toasts should have a role of [status](https://www.w3.org/TR/wai-aria-1.1/#status) or
   * [alert](https://www.w3.org/TR/wai-aria-1.1/#alert) to signal assistive technologies
   * that it requires the user's attention. In general, you will always want to use the less
   * strict "status" role.
   */
  role: PropTypes.oneOf(['status', 'alert'])
};
Toast.defaultProps = {
  appearance: 'info',
  closeButton: /*#__PURE__*/React.createElement(CloseButton, null),
  icon: statusIconAppearance
};

/** @component */
export { Toast };